import React, { useEffect } from 'react';
import { useVesselStore } from 'src/context/VesselContext';
import { useStowageStore } from 'src/context/StowageContext';
import { TankPlan, TankColumn, OverlayMenuEnum, TankSectionEnum } from 'src/types/ViewModel/Vessel/TankPlan';
import TankPlanGrid from './TankPlanGrid';
import TankContextMenu from './TankPlanMenus/TankContextMenu';
import TankDetails from './TankPlanMenus/TankDetailsContainer';
import TankPlanBottomPanel from './TankPlanMenus/TankPlanBottomPanel';
import TankPlanTopMenu from './TankPlanMenus/TankPlanTopMenu';
import TankPreviousCargoes from './TankPreviousCargoesContainer';
import {
    getColumnEndFrame,
    getTanksInColumn,
    getRows,
    getMaxNrOfTanksPerRow,
    getMaxNrOfRows,
    getSortedDeckTanks,
} from 'src/utils/stowage/tankPlanGridUtils';
import { calculateDeckTankPositions } from 'src/utils/stowage/tankPositionUtils';

const TankPlanContainer: React.FC = () => {
    const { selectedVessel } = useVesselStore();
    const { setTankPlan, tankContextMenuState, overlayMenuState } = useStowageStore();

    useEffect(() => {
        if (selectedVessel) {
            setUpTankPlan();
        }
    }, [selectedVessel]);

    const setUpTankPlan = (): void => {
        if (!selectedVessel) return;

        const internalTanks = selectedVessel.cargoTanks.filter((tank) => tank.section !== TankSectionEnum.Deck);
        if (!internalTanks.length) {
            setTankPlan({ deckTanks: [], tankColumns: [], shipMaxRows: 0 });
            return;
        }

        const startFrames = new Set(internalTanks.map((tank) => tank.startFrame));
        const sortedFrames = Array.from(startFrames).sort((a, b) => a - b);
        const tankPlan: TankPlan = { deckTanks: [], tankColumns: [], shipMaxRows: 0 };
        let previousColumnEndFrame = -1;

        sortedFrames.forEach((currentFrame) => {
            if (previousColumnEndFrame > currentFrame) return;

            const columnEndFrame = getColumnEndFrame(internalTanks, currentFrame);
            const tanksInColumn = getTanksInColumn(internalTanks, previousColumnEndFrame, currentFrame, columnEndFrame);
            previousColumnEndFrame = columnEndFrame;
            const rows = getRows(tanksInColumn);

            const column: TankColumn = {
                tankRows: rows,
                flexWidth: getMaxNrOfTanksPerRow(rows),
                startFrame: currentFrame,
                endFrame: columnEndFrame,
            };

            tankPlan.tankColumns.push(column);
        });

        tankPlan.shipMaxRows = getMaxNrOfRows(tankPlan.tankColumns);
        tankPlan.deckTanks = getSortedDeckTanks(selectedVessel.cargoTanks);

        const firstTank = tankPlan.tankColumns[0].tankRows[0].tanks[0];
        const lastTank =
            tankPlan.tankColumns[tankPlan.tankColumns.length - 1].tankRows[0].tanks[
                tankPlan.tankColumns[tankPlan.tankColumns.length - 1].tankRows[0].tanks.length - 1
            ];

        calculateDeckTankPositions(tankPlan.deckTanks, firstTank, lastTank);
        setTankPlan(tankPlan);
    };

    return (
        <>
            {tankContextMenuState && tankContextMenuState.visible && <TankContextMenu contextMenuState={tankContextMenuState} />}
            {overlayMenuState.visible && overlayMenuState.type === OverlayMenuEnum.TankDetails && <TankDetails tank={overlayMenuState.tank} />}
            {overlayMenuState.visible && overlayMenuState.type === OverlayMenuEnum.PreviousCargoes && (
                <TankPreviousCargoes tank={overlayMenuState.tank} />
            )}

            <TankPlanTopMenu />
            <TankPlanGrid />
            <TankPlanBottomPanel />
        </>
    );
};

export default TankPlanContainer;
